import type { ScheduledShow } from '@shady-pines/db';
import { omit } from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useApiStore } from '../api/api-store';
import { ShowApi } from './show-api';
import type { ShowState } from './types';

type UtilityFunctions = {
  setCurrentlyAiringShow: (a: ScheduledShow | null) => void;
  setUpcomingShows: (upcomingShows: ScheduledShow[]) => void;
  setInitialLoadCompleted: () => void;
};

const initialState = {
  loading: {
    shows: false,
    subscriptions: false,
    updateSubscriptionsById: {},
    updateShowById: {},
    fetchSubscribersToMyShow: {},
    initial: true,
  },

  currentlyAiringShow: null,
  shows: null,
  showSubscriptionsById: null,
  myShowSubscriptions: {},
  upcomingShows: [],
  lastShowRefresh: null,
  lastDataFetchedFromApiAt: null,
} as const satisfies ShowState;

export const useShowStore = create<ShowState & ShowApi & UtilityFunctions>()(
  persist(
    (set, get) => ({
      ...initialState,
      ...ShowApi(get, set),

      setCurrentlyAiringShow: (currentlyAiringShow) => {
        set({
          currentlyAiringShow,
        } as const satisfies Partial<ShowState>);
      },
      setUpcomingShows: (upcomingShows) => {
        set({
          upcomingShows,
        } as const satisfies Partial<ShowState>);
      },
      setInitialLoadCompleted: () =>
        set(
          (s) =>
            ({
              loading: {
                ...s.loading,
                initial: false,
              },
            }) as const satisfies Partial<ShowState>,
        ),
    }),
    {
      name: 'show-storage',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const store = useApiStore.getState().localStores.show;
          return store?.getItem(name) ?? null;
        },
        setItem: (name: string, value: string) => {
          const store = useApiStore.getState().localStores.show;
          store?.setItem(name, value);
        },
        removeItem: (name: string) => {
          const store = useApiStore.getState().localStores.show;
          store?.removeItem(name);
        },
      })),
      skipHydration: true,
      partialize: (state) => omit(state, 'loading'),
    },
  ),
);
